import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/schemas';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import NvRouter from 'shared/components/nv-router';
import Root from 'athena/components/mentoring-program/root';
import MyMenteesList from 'athena/components/mentoring-program/my-mentees-list';
import Flyer from 'athena/components/mentoring-program/flyer';
import { getMentoringProgram } from 'redux/selectors/mentoring-programs';
import { useProgramSummaryCounts } from 'athena/hooks/use-program-summary-counts';
import useTimeZone from 'athena/hooks/use-time-zone';
import MentoringProgramContext, { MentoringProgramError } from 'athena/components/mentoring-program/context';
import { getMentoringProgram as fetchMentoringProgram } from 'redux/actions/mentoring-programs';
import { fetchMeetingPlan } from 'redux/actions/mentoring-program-sessions';
import ProgramConfiguration from './program-configuration';
import ManageProgramAccessModal from '../manage-program-access-modal';


const contentRoutes = [
  {
    path: '/settings',
    component: ProgramConfiguration,
  },
  {
    path: '/my-mentees',
    component: MyMenteesList,
  },
  {
    path: '/recruitments/',
    component: Flyer,
  },
  {
    path: '/',
    component: Root,
  },
];

const MentoringProgram = () => {
  const dispatch = useAppDispatch();
  const countState = useProgramSummaryCounts();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<MentoringProgramError>(null);
  const [programName, setProgramName] = useState('');
  const { injectServices } = useContext(AngularContext);
  const [$state, AlertMessages] = injectServices(['$state', 'AlertMessages']);
  const { programId, connectionId } = $state.params;
  const isFlyer = $state.current?.name === 'mentoring-program-flyer';
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [showFindMentorModal, setShowFindMentorModal] = useState(false);

  const routerBasename = `/#!/mentoring-programs/${programId}`;

  const timeZone = useTimeZone();
  const mentoringProgram = useSelector((state: RootState) => (
    getMentoringProgram(state, programId)
  )) ?? { name: programName };

  const styles = css`
    height: ${error ? '100%' : '100vh'};
  `;

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchMentoringProgram(programId)).then((response) => {
      if (response.error) {
        const { message, mentorshipProgramName } = JSON.parse(response.error?.message) ?? {};
        if (message === MentoringProgramError.USER_DROPPED) {
          setError(MentoringProgramError.USER_DROPPED);
          setProgramName(mentorshipProgramName);
        } else if (!isFlyer) {
          setError(MentoringProgramError.USER_NOT_AUTHORIZED);
          $state.go('dashboard').then(() => {
            AlertMessages.error('', 'COURSE_HOME.HEADER.COURSE_ACCESS_DENIED');
          });
        }
      } else {
        const { payload: { isEnrolled } } = response;
        if (!isEnrolled && !isFlyer) {
          setShowManageAccess(true);
        }
      }
      setIsLoading(false);
    });
  }, [dispatch, programId]);

  useEffect(() => {
    if (connectionId) {
      dispatch(fetchMeetingPlan({
        programId,
        mentorshipProgramConnectionId: connectionId,
      }));
    }
  }, [dispatch, connectionId]);

  const contextValue = {
    isLoading,
    countState,
    mentoringProgram: {
      ...mentoringProgram,
      showFindMentorModal,
      setShowFindMentorModal,
    },
    error,
    timeZone,
  };

  return (
    <>
      <MentoringProgramContext.Provider value={contextValue}>
        <div className='d-flex' css={styles}>
          <NvRouter routes={contentRoutes} basename={routerBasename} />
        </div>
      </MentoringProgramContext.Provider>
      <ManageProgramAccessModal
        onClose={() => setShowManageAccess(false)}
        programId={showManageAccess ? programId : undefined}
      />
    </>
  );
};

export default MentoringProgram;
